import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Calendar,
  MapPin,
  Mail,
  Phone,
  FileText,
  Trophy,
  ExternalLink,
  Users,
  Clock,
  ChevronDown,
  ChevronUp,
  Download
} from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { getTournament } from '../services/tournamentService';
import { getDraw } from '../services/drawService';
import TournamentHero from './components/TournamentHero';

const TournamentDetailPage = () => {
  const { isDarkMode } = useTheme();
  const { id } = useParams();
  const [tournament, setTournament] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Draw state
  const [draw, setDraw] = useState([]);
  const [loadingDraw, setLoadingDraw] = useState(false);
  const [showAllGroups, setShowAllGroups] = useState(false);

  useEffect(() => {
    fetchTournament();
  }, [id]);

  const fetchTournament = async () => {
    try {
      setLoading(true);
      const response = await getTournament(id);
      setTournament(response.data);
      
      // Fetch draw after tournament loads
      if (response.data?.id) {
        fetchDraw(response.data.id);
      }
    } catch (error) {
      console.error('Error fetching tournament:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDraw = async (tournamentId) => {
    try {
      setLoadingDraw(true);
      const response = await getDraw(tournamentId);
      setDraw(response.data || []);
    } catch (error) {
      console.error('Error fetching draw:', error);
      setDraw([]);
    } finally {
      setLoadingDraw(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'TBA';
    return new Date(dateString).toLocaleDateString('en-ZA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Group players by tee time or group number
  const getGroupedDraw = () => {
    if (!draw || draw.length === 0) return [];

    const groups = {};
    
    draw.forEach(player => {
      // Use group_number if available, otherwise tee_time, otherwise 'Ungrouped'
      const groupKey = player.group_number 
        ? `Group ${player.group_number}` 
        : player.tee_time 
          ? player.tee_time 
          : 'Ungrouped';
      
      if (!groups[groupKey]) {
        groups[groupKey] = {
          key: groupKey,
          teeTime: player.tee_time || '',
          startingTee: player.starting_tee || '1',
          players: []
        };
      }
      groups[groupKey].players.push(player);
    });

    // Sort groups by tee time or group number
    return Object.values(groups).sort((a, b) => {
      if (a.teeTime && b.teeTime) {
        return a.teeTime.localeCompare(b.teeTime);
      }
      return a.key.localeCompare(b.key);
    });
  };

  const groupedDraw = getGroupedDraw();
  const displayedGroups = showAllGroups ? groupedDraw : groupedDraw.slice(0, 6);
  const hasMoreGroups = groupedDraw.length > 6;

  // Helper to determine entry form type
  const getEntryFormInfo = () => {
    if (!tournament) return { type: 'none', url: null };
    
    // Check explicit entry_form_type first
    if (tournament.entry_form_type === 'pdf' && tournament.entry_form_url) {
      return { type: 'pdf', url: tournament.entry_form_url, filename: tournament.entry_form_pdf_filename };
    }
    
    if (tournament.entry_form_type === 'link' && (tournament.entry_form_url || tournament.registration_link)) {
      return { type: 'link', url: tournament.entry_form_url || tournament.registration_link };
    }
    
    // Fallback: check registration_link (backwards compatibility)
    if (tournament.registration_link) {
      // Check if it's a PDF URL
      if (tournament.registration_link.toLowerCase().endsWith('.pdf')) {
        return { type: 'pdf', url: tournament.registration_link };
      }
      return { type: 'link', url: tournament.registration_link };
    }
    
    return { type: 'none', url: null };
  };

  const entryFormInfo = tournament ? getEntryFormInfo() : { type: 'none', url: null };

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${
        isDarkMode ? 'bg-fsgu-gray-900' : 'bg-fsgu-silver-50'
      }`}>
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-fsgu-green-500 mb-4"></div>
          <p className={isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'}>
            Loading tournament...
          </p>
        </div>
      </div>
    );
  }

  if (!tournament) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${
        isDarkMode ? 'bg-fsgu-gray-900' : 'bg-fsgu-silver-50'
      }`}>
        <div className="text-center">
          <Trophy className={`w-16 h-16 mx-auto mb-4 ${
            isDarkMode ? 'text-fsgu-silver-600' : 'text-fsgu-silver-400'
          }`} />
          <h2 className={`text-2xl font-bold mb-2 ${
            isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
          }`}>
            Tournament Not Found
          </h2>
          <Link
            to="/tournaments"
            className="btn btn-primary inline-flex items-center mt-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Tournaments
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${
      isDarkMode ? 'bg-fsgu-gray-900' : 'bg-fsgu-silver-50'
    }`}>
      {/* Hero Section */}
      <TournamentHero tournament={tournament} />

      {/* Back Button */}
      <div className="container-custom py-6">
        <Link
          to="/tournaments"
          className={`inline-flex items-center px-4 py-2.5 rounded-lg font-medium transition-all ${
            isDarkMode
              ? 'bg-fsgu-gray-800 text-fsgu-silver-200 border border-fsgu-gray-700 hover:bg-fsgu-gray-700 hover:border-fsgu-green-500'
              : 'bg-fsgu-white text-fsgu-silver-700 border border-fsgu-silver-300 hover:bg-fsgu-silver-50 hover:border-fsgu-green-500'
          } shadow-sm hover:shadow-md`}
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back to Tournaments
        </Link>
      </div>

      {/* Main Content */}
      <section className="section pt-0">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content - 2/3 */}
            <div className="lg:col-span-2 space-y-8">
              {/* Description */}
              {tournament.description && (
                <div className={`p-6 rounded-xl border ${
                  isDarkMode
                    ? 'bg-fsgu-gray-800 border-fsgu-gray-700'
                    : 'bg-fsgu-white border-fsgu-silver-200'
                }`}>
                  <p className={`text-lg ${
                    isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
                  }`}>
                    {tournament.description}
                  </p>
                </div>
              )}

              {/* Full Description */}
              {tournament.full_description && (
                <div className={`p-6 rounded-xl border ${
                  isDarkMode
                    ? 'bg-fsgu-gray-800 border-fsgu-gray-700'
                    : 'bg-fsgu-white border-fsgu-silver-200'
                }`}>
                  <h2 className={`text-2xl font-bold mb-4 flex items-center ${
                    isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'
                  }`}>
                    <FileText className="w-6 h-6 mr-2 text-fsgu-green-500" />
                    About Tournament
                  </h2>
                  <div className={`prose max-w-none ${
                    isDarkMode ? 'prose-invert' : ''
                  }`}>
                    <p className={`whitespace-pre-wrap ${
                      isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
                    }`}>
                      {tournament.full_description}
                    </p>
                  </div>
                </div>
              )}

              {/* Tournament Details */}
              <div className={`p-6 rounded-xl border ${
                isDarkMode
                  ? 'bg-fsgu-gray-800 border-fsgu-gray-700'
                  : 'bg-fsgu-white border-fsgu-silver-200'
              }`}>
                <h2 className={`text-2xl font-bold mb-6 ${
                  isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'
                }`}>
                  Tournament Details
                </h2>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <div className={`text-sm mb-1 ${
                      isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-500'
                    }`}>
                      Dates
                    </div>
                    <div className={`font-medium ${
                      isDarkMode ? 'text-fsgu-silver-200' : 'text-fsgu-silver-900'
                    }`}>
                      {formatDate(tournament.start_date)}
                      {tournament.start_date !== tournament.end_date && (
                        <> - {formatDate(tournament.end_date)}</>
                      )}
                    </div>
                  </div>

                  <div>
                    <div className={`text-sm mb-1 ${
                      isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-500'
                    }`}>
                      Format
                    </div>
                    <div className={`font-medium ${
                      isDarkMode ? 'text-fsgu-silver-200' : 'text-fsgu-silver-900'
                    }`}>
                      {tournament.format || 'To be announced'}
                    </div>
                  </div>

                  <div>
                    <div className={`text-sm mb-1 ${
                      isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-500'
                    }`}>
                      Number of Rounds
                    </div>
                    <div className={`font-medium ${
                      isDarkMode ? 'text-fsgu-silver-200' : 'text-fsgu-silver-900'
                    }`}>
                      {tournament.rounds || 1}
                    </div>
                  </div>

                  <div>
                    <div className={`text-sm mb-1 ${
                      isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-500'
                    }`}>
                      Category
                    </div>
                    <div className={`font-medium ${
                      isDarkMode ? 'text-fsgu-silver-200' : 'text-fsgu-silver-900'
                    }`}>
                      {tournament.category?.replace(/_/g, ' ') || 'Open'}
                    </div>
                  </div>
                </div>
              </div>

              {/* Draw / Tee Sheet Section */}
              {draw.length > 0 && (
                <div className={`p-6 rounded-xl border ${
                  isDarkMode
                    ? 'bg-fsgu-gray-800 border-fsgu-gray-700'
                    : 'bg-fsgu-white border-fsgu-silver-200'
                }`}>
                  <h2 className={`text-2xl font-bold mb-2 flex items-center ${
                    isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'
                  }`}>
                    <Users className="w-6 h-6 mr-2 text-fsgu-green-500" />
                    Draw / Tee Sheet
                  </h2>
                  <p className={`text-sm mb-6 ${
                    isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
                  }`}>
                    {draw.length} players in the field
                  </p>

                  {/* Groups Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {displayedGroups.map((group, idx) => (
                      <div
                        key={idx}
                        className={`rounded-lg border overflow-hidden ${
                          isDarkMode
                            ? 'bg-fsgu-gray-900 border-fsgu-gray-700'
                            : 'bg-fsgu-silver-50 border-fsgu-silver-200'
                        }`}
                      >
                        {/* Group Header */}
                        <div className={`px-4 py-2 flex items-center justify-between ${
                          isDarkMode
                            ? 'bg-fsgu-gray-700'
                            : 'bg-fsgu-silver-200'
                        }`}>
                          <div className="flex items-center gap-3">
                            {group.teeTime && (
                              <span className={`flex items-center text-sm font-semibold ${
                                isDarkMode ? 'text-fsgu-green-400' : 'text-fsgu-green-600'
                              }`}>
                                <Clock className="w-4 h-4 mr-1" />
                                {group.teeTime}
                              </span>
                            )}
                            <span className={`text-sm font-medium ${
                              isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
                            }`}>
                              {group.key}
                            </span>
                          </div>
                          {group.startingTee && group.startingTee !== '1' && (
                            <span className={`text-xs px-2 py-0.5 rounded ${
                              isDarkMode
                                ? 'bg-fsgu-gray-600 text-fsgu-silver-400'
                                : 'bg-fsgu-silver-300 text-fsgu-silver-600'
                            }`}>
                              Tee {group.startingTee}
                            </span>
                          )}
                        </div>

                        {/* Players */}
                        <div className="divide-y divide-fsgu-gray-700">
                          {group.players.map((player, pIdx) => (
                            <div
                              key={pIdx}
                              className={`px-4 py-2.5 flex items-center justify-between ${
                                isDarkMode
                                  ? 'divide-fsgu-gray-700'
                                  : 'divide-fsgu-silver-200'
                              }`}
                            >
                              <div>
                                <div className={`font-medium ${
                                  isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'
                                }`}>
                                  {player.player_name}
                                </div>
                                {player.club && (
                                  <div className={`text-xs ${
                                    isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-500'
                                  }`}>
                                    {player.club}
                                  </div>
                                )}
                              </div>
                              {player.handicap !== null && player.handicap !== undefined && (
                                <span className={`text-sm font-medium px-2 py-0.5 rounded ${
                                  isDarkMode
                                    ? 'bg-fsgu-gray-700 text-fsgu-silver-300'
                                    : 'bg-fsgu-silver-200 text-fsgu-silver-700'
                                }`}>
                                  {player.handicap > 0 ? '+' : ''}{player.handicap}
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Show More/Less Button */}
                  {hasMoreGroups && (
                    <div className="mt-6 text-center">
                      <button
                        onClick={() => setShowAllGroups(!showAllGroups)}
                        className={`inline-flex items-center px-4 py-2 rounded-lg font-medium transition-colors ${
                          isDarkMode
                            ? 'bg-fsgu-gray-700 text-fsgu-silver-200 hover:bg-fsgu-gray-600'
                            : 'bg-fsgu-silver-200 text-fsgu-silver-700 hover:bg-fsgu-silver-300'
                        }`}
                      >
                        {showAllGroups ? (
                          <>
                            <ChevronUp className="w-4 h-4 mr-2" />
                            Show Less
                          </>
                        ) : (
                          <>
                            <ChevronDown className="w-4 h-4 mr-2" />
                            Show All {groupedDraw.length} Groups
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Loading Draw */}
              {loadingDraw && (
                <div className={`p-6 rounded-xl border text-center ${
                  isDarkMode
                    ? 'bg-fsgu-gray-800 border-fsgu-gray-700'
                    : 'bg-fsgu-white border-fsgu-silver-200'
                }`}>
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-fsgu-green-500 mb-3"></div>
                  <p className={isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'}>
                    Loading draw...
                  </p>
                </div>
              )}

              {/* Rules */}
              {tournament.rules && (
                <div className={`p-6 rounded-xl border ${
                  isDarkMode
                    ? 'bg-fsgu-gray-800 border-fsgu-gray-700'
                    : 'bg-fsgu-white border-fsgu-silver-200'
                }`}>
                  <h2 className={`text-2xl font-bold mb-4 ${
                    isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'
                  }`}>
                    Tournament Rules
                  </h2>
                  <div className={`whitespace-pre-wrap ${
                    isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
                  }`}>
                    {tournament.rules}
                  </div>
                </div>
              )}

              {/* Venue Details */}
              <div className={`p-6 rounded-xl border ${
                isDarkMode
                  ? 'bg-fsgu-gray-800 border-fsgu-gray-700'
                  : 'bg-fsgu-white border-fsgu-silver-200'
              }`}>
                <h2 className={`text-2xl font-bold mb-6 flex items-center ${
                  isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'
                }`}>
                  <MapPin className="w-6 h-6 mr-2 text-fsgu-blue-500" />
                  Venue Information
                </h2>

                <div className="space-y-4">
                  <div>
                    <div className={`text-sm mb-1 ${
                      isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-500'
                    }`}>
                      Venue
                    </div>
                    <div className={`text-lg font-semibold ${
                      isDarkMode ? 'text-fsgu-silver-200' : 'text-fsgu-silver-900'
                    }`}>
                      {tournament.venue_name}
                    </div>
                  </div>

                  {tournament.venue_address && (
                    <div>
                      <div className={`text-sm mb-1 ${
                        isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-500'
                      }`}>
                        Address
                      </div>
                      <div className={`${
                        isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
                      }`}>
                        {tournament.venue_address}
                        {tournament.venue_city && `, ${tournament.venue_city}`}
                      </div>
                    </div>
                  )}

                  {tournament.special_notes && (
                    <div className={`mt-4 p-4 rounded-lg ${
                      isDarkMode ? 'bg-fsgu-gray-700' : 'bg-fsgu-silver-50'
                    }`}>
                      <div className={`text-sm font-medium mb-2 ${
                        isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
                      }`}>
                        Special Notes
                      </div>
                      <div className={`text-sm ${
                        isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
                      }`}>
                        {tournament.special_notes}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Sidebar - 1/3 */}
            <div className="space-y-6">
              {/* Registration Card - UPDATED: Supports PDF and Link */}
              {entryFormInfo.type !== 'none' && tournament.status === 'published' && (
                <div className={`p-6 rounded-2xl border shadow-lg ${
                  isDarkMode
                    ? 'bg-fsgu-gray-800 border-fsgu-gray-700'
                    : 'bg-fsgu-white border-fsgu-silver-200'
                }`}>
                  <div className="text-center mb-4">
                    <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full mb-3 ${
                      isDarkMode ? 'bg-fsgu-green-500/20' : 'bg-fsgu-green-500/10'
                    }`}>
                      <Trophy className={`w-8 h-8 ${
                        isDarkMode ? 'text-fsgu-green-400' : 'text-fsgu-green-600'
                      }`} />
                    </div>
                    <h3 className={`text-xl font-bold mb-2 ${
                      isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'
                    }`}>
                      Enter Tournament
                    </h3>
                    <p className={`text-sm ${
                      isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
                    }`}>
                      {entryFormInfo.type === 'pdf' 
                        ? 'Download the entry form to register'
                        : 'Registration is handled via Golf Genius'
                      }
                    </p>
                  </div>

                  {/* PDF Download Button */}
                  {entryFormInfo.type === 'pdf' && (
                    <>
                      <a
                        href={entryFormInfo.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        download
                        className="btn btn-primary w-full inline-flex items-center justify-center mb-3"
                      >
                        <Download className="w-5 h-5 mr-2" />
                        Download Entry Form
                      </a>
                      <p className={`text-xs text-center ${
                        isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-500'
                      }`}>
                        PDF • Complete and submit to FSGU
                      </p>
                    </>
                  )}

                  {/* Golf Genius / External Link Button */}
                  {entryFormInfo.type === 'link' && (
                    <>
                      <a
                        href={entryFormInfo.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-primary w-full inline-flex items-center justify-center"
                      >
                        <ExternalLink className="w-5 h-5 mr-2" />
                        Register on Golf Genius
                      </a>
                      <p className={`text-xs text-center mt-3 ${
                        isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-500'
                      }`}>
                        You will be redirected to the Golf Genius platform
                      </p>
                    </>
                  )}
                </div>
              )}

              {/* No registration link message */}
              {entryFormInfo.type === 'none' && tournament.status === 'published' && (
                <div className={`p-6 rounded-2xl border ${
                  isDarkMode
                    ? 'bg-fsgu-gray-800 border-fsgu-gray-700'
                    : 'bg-fsgu-white border-fsgu-silver-200'
                }`}>
                  <div className="text-center">
                    <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full mb-3 ${
                      isDarkMode ? 'bg-fsgu-gold-500/20' : 'bg-fsgu-gold-500/10'
                    }`}>
                      <Calendar className={`w-8 h-8 ${
                        isDarkMode ? 'text-fsgu-gold-400' : 'text-fsgu-gold-600'
                      }`} />
                    </div>
                    <h3 className={`text-lg font-bold mb-2 ${
                      isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'
                    }`}>
                      Registration Coming Soon
                    </h3>
                    <p className={`text-sm ${
                      isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
                    }`}>
                      Registration details will be available shortly. Check back soon or contact FSGU for more information.
                    </p>
                  </div>
                </div>
              )}

              {/* Tournament Status (for non-published) */}
              {tournament.status !== 'published' && tournament.status !== 'draft' && (
                <div className={`p-6 rounded-2xl border ${
                  isDarkMode
                    ? 'bg-fsgu-gray-800 border-fsgu-gray-700'
                    : 'bg-fsgu-white border-fsgu-silver-200'
                }`}>
                  <div className="text-center">
                    <div className={`inline-block px-4 py-2 rounded-full text-sm font-medium mb-3 ${
                      tournament.status === 'in_progress'
                        ? isDarkMode ? 'bg-fsgu-blue-500/20 text-fsgu-blue-400' : 'bg-fsgu-blue-100 text-fsgu-blue-700'
                        : tournament.status === 'completed'
                        ? isDarkMode ? 'bg-fsgu-green-500/20 text-fsgu-green-400' : 'bg-fsgu-green-100 text-fsgu-green-700'
                        : isDarkMode ? 'bg-fsgu-gray-700 text-fsgu-silver-400' : 'bg-fsgu-silver-100 text-fsgu-silver-600'
                    }`}>
                      {tournament.status === 'in_progress' && 'Tournament In Progress'}
                      {tournament.status === 'completed' && 'Tournament Completed'}
                      {tournament.status === 'cancelled' && 'Tournament Cancelled'}
                    </div>
                  </div>
                </div>
              )}

              {/* Field Size - Show if draw has data */}
              {draw.length > 0 && (
                <div className={`p-6 rounded-xl border ${
                  isDarkMode
                    ? 'bg-fsgu-gray-800 border-fsgu-gray-700'
                    : 'bg-fsgu-white border-fsgu-silver-200'
                }`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className={`text-sm ${
                        isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-500'
                      }`}>
                        Field Size
                      </div>
                      <div className={`text-2xl font-bold ${
                        isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'
                      }`}>
                        {draw.length}
                      </div>
                    </div>
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                      isDarkMode ? 'bg-fsgu-green-500/20' : 'bg-fsgu-green-500/10'
                    }`}>
                      <Users className={`w-6 h-6 ${
                        isDarkMode ? 'text-fsgu-green-400' : 'text-fsgu-green-600'
                      }`} />
                    </div>
                  </div>
                  <p className={`text-xs mt-2 ${
                    isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-500'
                  }`}>
                    Players confirmed in draw
                  </p>
                </div>
              )}

              {/* Contact Information */}
              {(tournament.contact_person || tournament.contact_email || tournament.contact_phone) && (
                <div className={`p-6 rounded-xl border ${
                  isDarkMode
                    ? 'bg-fsgu-gray-800 border-fsgu-gray-700'
                    : 'bg-fsgu-white border-fsgu-silver-200'
                }`}>
                  <h3 className={`text-lg font-semibold mb-4 ${
                    isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'
                  }`}>
                    Contact
                  </h3>

                  <div className="space-y-3">
                    {tournament.contact_person && (
                      <div>
                        <div className={`text-xs mb-1 ${
                          isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-500'
                        }`}>
                          Tournament Director
                        </div>
                        <div className={`font-medium ${
                          isDarkMode ? 'text-fsgu-silver-200' : 'text-fsgu-silver-900'
                        }`}>
                          {tournament.contact_person}
                        </div>
                      </div>
                    )}

                    {tournament.contact_email && (
                      <div className="flex items-center">
                        <Mail className={`w-4 h-4 mr-2 ${
                          isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-400'
                        }`} />
                        <a
                          href={`mailto:${tournament.contact_email}`}
                          className={`text-sm hover:underline ${
                            isDarkMode ? 'text-fsgu-blue-400' : 'text-fsgu-blue-600'
                          }`}
                        >
                          {tournament.contact_email}
                        </a>
                      </div>
                    )}

                    {tournament.contact_phone && (
                      <div className="flex items-center">
                        <Phone className={`w-4 h-4 mr-2 ${
                          isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-400'
                        }`} />
                        <a
                          href={`tel:${tournament.contact_phone}`}
                          className={`text-sm hover:underline ${
                            isDarkMode ? 'text-fsgu-blue-400' : 'text-fsgu-blue-600'
                          }`}
                        >
                          {tournament.contact_phone}
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TournamentDetailPage;